import { inject, Injectable } from '@angular/core';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { AccountVisibilityError, AccountVisibilityService, HideAccountRequest } from '@core/services/products/account-visibility.service';
import { AlertController, LoadingController, ModalController } from '@ionic/angular';
import { Product } from '@shared/models/products.model';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class HideAccountService {
  private readonly loadingController = inject(LoadingController);
  private readonly alertController = inject(AlertController);
  private readonly modalController = inject(ModalController);
  private readonly navigationService = inject(NavigationService);
  private readonly accountVisibilityService = inject(AccountVisibilityService);

  /**
   * Выполняет операцию скрытия счета
   */
  public async hideAccount(request: HideAccountRequest, product: Product): Promise<void> {
    const loading = await this.loadingController.create({
      message: 'Скрытие счета...'
    });

    try {
      await loading.present();
      await this.accountVisibilityService.hideAccount(request).toPromise();
      await loading.dismiss();
      await this.showSuccessMessage(product);
    } catch (error) {
      await loading.dismiss();
      await this.handleError(error);
      throw error; // Пробрасываем ошибку дальше для обработки в компонентах
    }
  }

  private async showSuccessMessage(product: Product): Promise<void> {
    const accountNumber = product?.number || product?.formattedNumber || product?.id || '';

    const modal = await this.modalController.create({
      component: (await import('@modals/result-payment/result-payment.component')).ResultPaymentComponent,
      cssClass: 'operation-detail',
      backdropDismiss: false,
      handle: false,
      initialBreakpoint: 0.4,
      componentProps: {
        title: `Счет ${accountNumber} от ${dayjs(product.beginDate).format('DD.MM.YYYY')} успешно скрыт`,
        message: `Для раскрытия карт и счетов перейдите в ваш профиль и нажмите на кнопку "Отобразить".`,
        shortMode: true
      }
    });

    await modal.present();
    await modal.onWillDismiss();
    await this.navigationService.setRoot('/main');
  }

  private async handleError(error: unknown): Promise<void> {
    let message = 'Произошла ошибка при скрытии счета. Попробуйте позже.';

    if (error instanceof AccountVisibilityError) {
      message = error.userMessage;
    }

    const alert = await this.alertController.create({
      header: 'Ошибка',
      message,
      buttons: ['OK']
    });

    await alert.present();
  }
}
